import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { PeopleApi, isLegalAgreementRequiredError } from '../api';
import { RoutePath } from '../config';
import { User } from '../model';
import { useAuthContext } from './useAuthContext';

export function useProfile(isClient = false): User | undefined {
  const { signOut, isAuthorized, isLoading: authLoading } = useAuthContext();
  const location = useLocation();
  const isOnLoginPage =
    location.pathname === RoutePath.ClientLogin ||
    location.pathname === RoutePath.DoctorLogin;

  // Не делаем запрос если:
  // 1. Идет загрузка авторизации
  // 2. Пользователь не авторизован
  // 3. На странице логина
  const shouldFetch = !authLoading && isAuthorized && !isOnLoginPage;

  const { data: profile } = useQuery({
    queryKey: [PeopleApi.KEY, 'me', isClient],
    queryFn: () => PeopleApi.me(isClient),
    enabled: shouldFetch, // Не делать запрос если не авторизован или на странице логина
    // После логина /client/people иногда может кратковременно вернуть 401/403 (гонка с сессией/cookie).
    // Даем несколько попыток перед тем как выкидывать пользователя.
    retry(failureCount, error: unknown) {
      if (isLegalAgreementRequiredError(error)) {
        return false;
      }
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) {
        return failureCount < 3;
      }
      return false;
    },
    retryDelay: 400,
    onError(error: unknown) {
      // Re-consent: не разлогинивать на машинном коде legal_agreement_required.
      if (isLegalAgreementRequiredError(error)) {
        return;
      }

      const lastSignInAtRaw = sessionStorage.getItem('lastSignInAt');
      const lastSignInAt = lastSignInAtRaw ? Number(lastSignInAtRaw) : 0;
      const isInGracePeriod =
        lastSignInAt > 0 && Date.now() - lastSignInAt < 2500;

      const status = (error as { response?: { status?: number } })?.response
        ?.status;

      // Предотвращаем бесконечный цикл - не вызываем signOut если уже на странице логина
      if (
        (status === 401 || status === 403) &&
        !isOnLoginPage &&
        isAuthorized &&
        !isInGracePeriod
      ) {
        // Используем setTimeout чтобы избежать множественных вызовов signOut
        setTimeout(() => {
          signOut();
        }, 0);
      }
    },
  });

  return profile;
}
